/**
 * FLAG WORKFLOW UTILITIES
 *
 * Centralized, reusable functions for flag operations.
 * All functions use atomic backend workflows - no race conditions!
 *
 * Usage:
 *   import { flagDrone, clearFlag } from '../utils/flag-utils';
 *   await flagDrone(api, serialnumber, description);
 *
 * These replace Promise.all([patch, post]) patterns with single atomic calls.
 *
 * NOTE: user_id is automatically taken from JWT authentication on the backend.
 * No need to pass userid in the request body.
 */

/**
 * Set drone status (Flag or OOC) - atomic operation
 *
 * Creates flag and updates drone condition in ONE transaction.
 * Works for both 'Flag' and 'OOC' status.
 * Rejects if drone already has an active flag (must clear first).
 *
 * @param {object} api - API helper
 * @param {string} serialnumber - Drone serial number
 * @param {string} description - Issue description
 * @param {string} status - 'Flag' or 'OOC'
 * @returns {Promise<object>} - { success: true, flag, drone }
 */
export const setStatus = async (api, serialnumber, description, status = 'Flag') => {
    try {
        const result = await api.post('/workflows/set-status', {
            body: {
                serialnumber,
                description,
                status
            }
        });

        return result;
    } catch (error) {
        console.error('Error setting drone status:', error);
        throw error;
    }
};

// Convenience wrappers for clarity
export const flagDrone = (api, serialnumber, description) =>
    setStatus(api, serialnumber, description, 'Flag');

export const markOOC = (api, serialnumber, description) =>
    setStatus(api, serialnumber, description, 'OOC');

/**
 * Clear a flag (atomic operation)
 *
 * Deactivates flag and resets drone to RTF in ONE transaction.
 *
 * @param {object} api - API helper
 * @param {string} serialnumber - Drone serial number
 * @returns {Promise<object>} - { success: true }
 */
export const clearFlag = async (api, serialnumber) => {
    try {
        const result = await api.post('/workflows/clear-flag', {
            body: { serialnumber }
        });

        return result;
    } catch (error) {
        console.error('Error clearing flag:', error);
        throw error;
    }
};

/**
 * What to tell a person whose clear-flag was refused. The server writes the
 * sentence (a repair that is short of parts, or needs someone with manufacturing),
 * and names the parts in `missing` or `short`; the generic line is only for a
 * failure that carries nothing.
 *
 * @param {Error & {data?: object}} error
 * @returns {string}
 */
export const clearFlagErrorMessage = (error) => {
    const data = error?.data || {};
    const sentence = data.error?.message || data.error || '';
    if (!sentence) return 'The flag was not cleared. Try again.';
    const names = [...(data.missing || []), ...(data.short || [])]
        .map((p) => p?.target_name)
        .filter(Boolean);
    const unmentioned = names.filter((n) => !sentence.includes(n));
    return unmentioned.length ? `${sentence} Short: ${unmentioned.join(', ')}.` : sentence;
};

/**
 * Log maintenance
 *
 * Creates a maintenance log entry.
 * Does NOT clear flags or change drone condition.
 * User must separately call clearFlag() if issue is resolved.
 *
 * @param {object} api - API helper
 * @param {string} serialnumber - Drone serial number
 * @param {string} description - Maintenance description
 * @returns {Promise<object>} - { success: true, maintenanceLog }
 */
export const logMaintenance = async (api, serialnumber, description) => {
    try {
        const result = await api.post('/workflows/log-maintenance', {
            body: {
                serialnumber,
                description
            }
        });

        return result;
    } catch (error) {
        console.error('Error logging maintenance:', error);
        throw error;
    }
};

/**
 * Bulk clear flags (atomic operation)
 *
 * Clears flags AND logs maintenance for multiple drones in ONE transaction.
 * All succeed or all fail together - no partial state.
 *
 * @param {object} api - API helper
 * @param {string[]} serialnumbers - Array of drone serial numbers
 * @param {string} description - Maintenance log description
 * @returns {Promise<object>} - { success: true, processed: number, results: array }
 */
export const bulkClearFlags = async (api, serialnumbers, description) => {
    try {
        const result = await api.post('/workflows/bulk-clear-flags', {
            body: {
                serialnumbers,
                description
            }
        });

        return result;
    } catch (error) {
        console.error('Error in bulk clear flags:', error);
        throw error;
    }
};

/**
 * Bulk add flags (atomic operation)
 *
 * Adds flags to multiple drones in ONE transaction.
 * All succeed or all fail together - no partial state.
 *
 * @param {object} api - API helper
 * @param {Array<{serialnumber: string, description: string}>} drones - Array of drone flag data
 * @param {string} status - 'Flag' or 'OOC' (defaults to 'Flag')
 * @returns {Promise<object>} - { success: true, processed: number, results: array }
 */
export const bulkAddFlags = async (api, drones, status = 'Flag') => {
    try {
        const result = await api.post('/workflows/bulk-add-flags', {
            body: {
                drones,
                status
            }
        });

        return result;
    } catch (error) {
        console.error('Error in bulk add flags:', error);
        throw error;
    }
};
